import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import {
  AdminIcon,
  ManagementHeader,
  MetricCard,
  adminManagementStyles as styles,
  formatAdminDate,
} from '../../../components/admin/AdminManagement';
import { Alert } from '../../../components/Alert';
import { operationalApi } from '../../../features/reporting/operational.api';
import { toDisplayMessage } from '../../../lib/error-message';
import { formatNgn } from '../../../lib/format-currency';
import { formatStatusLabel } from '../../../lib/format-status';

export function AdminDiscountsReviewPage(): React.JSX.Element {
  const queue = useQuery({
    queryKey: ['admin', 'applications', 'discount-review'],
    queryFn: () => operationalApi.discountReviewQueue(1, 50),
  });
  const items = queue.data?.items ?? [];
  return (
    <div className={styles.page}>
      <ManagementHeader
        eyebrow="Application integrity"
        title="Discounts review"
        subtitle="Resolve legacy unverified discounts within the existing application workflow."
        icon="shield"
        context="Integrity queue"
      />
      <section className={styles.hero}>
        <div>
          <h2>Review discounts with complete provenance</h2>
          <p>Eligibility is defined by the backend and resolution remains on application detail.</p>
        </div>
        <strong>
          {queue.data ? `${queue.data.pagination.totalItems} to review` : 'Review queue'}
        </strong>
      </section>
      <section className={styles.metrics}>
        <MetricCard
          label="Needs review"
          value={queue.data?.pagination.totalItems ?? '—'}
          icon="warning"
        />
        <MetricCard label="Displayed" value={items.length} icon="document" tone="blue" />
        <MetricCard
          label="Provenance"
          value="Legacy"
          note="Unverified only"
          icon="shield"
          tone="amber"
        />
        <MetricCard
          label="Resolution"
          value="Detail"
          note="Authoritative workflow"
          icon="arrow"
          tone="violet"
        />
      </section>
      <div className={styles.contentGrid}>
        <section className={styles.mainCard}>
          <header className={styles.sectionHeader}>
            <div className={styles.sectionTitle}>
              <span className={styles.sectionIcon}>
                <AdminIcon name="shield" />
              </span>
              <div>
                <h2>Discount review queue</h2>
                <p>Eligible applications supplied by the backend</p>
              </div>
            </div>
            {queue.data && (
              <span className={styles.countBadge}>
                {queue.data.pagination.totalItems} applications
              </span>
            )}
          </header>
          {queue.isPending && <p className={styles.empty}>Loading discount review queue…</p>}
          {queue.isError && (
            <div className={styles.alertWrap}>
              <Alert tone="error">{toDisplayMessage(queue.error)}</Alert>
            </div>
          )}
          {queue.isSuccess && items.length === 0 && (
            <p className={styles.empty}>No legacy discounts currently require staff review.</p>
          )}
          {items.length > 0 && (
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Customer</th>
                    <th>Product</th>
                    <th>Discount</th>
                    <th>Provenance</th>
                    <th>Status</th>
                    <th>Submitted</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <tr key={item.subscriptionId}>
                      <td>
                        <div className={styles.primaryCell}>
                          <span className={styles.avatar}>
                            <AdminIcon name="customer" />
                          </span>
                          <span>
                            <strong>{item.customerDisplayName}</strong>
                            <small>Application customer</small>
                          </span>
                        </div>
                      </td>
                      <td>{item.productName ?? '—'}</td>
                      <td>
                        <strong>{formatNgn(item.discountAmount)}</strong>
                      </td>
                      <td>
                        <span className={`${styles.status} ${styles.warning}`}>
                          {formatStatusLabel(item.discountProvenance)}
                        </span>
                      </td>
                      <td>{formatStatusLabel(item.status)}</td>
                      <td>{formatAdminDate(item.submittedAt)}</td>
                      <td>
                        <Link to={item.detailRoute}>Review application →</Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
        <aside className={styles.rail}>
          <section className={styles.railCard}>
            <h2>What belongs here</h2>
            <p>
              Only non-zero, legacy-unverified discounts in reviewable application states. The
              backend owns this eligibility rule.
            </p>
          </section>
          <section className={styles.railCard}>
            <h2>Safe resolution</h2>
            <p>
              Authorize or remove the discount from application detail. This page introduces no new
              mutation or approval policy.
            </p>
          </section>
        </aside>
      </div>
    </div>
  );
}
